import { BigNumber, ethers, utils } from "ethers";
import factoryV3Abi from "../../abi/factoryV3.json";
import vaultAbi from "../../abi/balanceVault.json";
import helperAbi from "../../abi/helper.json";
import erc20Abi from "../../abi/erc20.json";
import uniV3Abi from "../../abi/uniV3.json";
import { getProof } from "./common";
import {
  showErrorAlert as showAlert,
  showErrorAlert,
  showPendingAlert,
} from "../showAlert";
import { getUserBorrowAmountFromLogs } from "./lendingPoolApi";
import { Pool } from "@uniswap/v3-sdk";
import { Token } from "@uniswap/sdk-core";
import uniV3PoolAbi from "../../abi/uniV3Pool.json";
import { UNI_POSITION_MANAGER } from "../constant/externalAddresses";
import { isSupportedChain, SupportedChainId } from "../constant/chains";
import { RPC_PROVIDERS } from "../providers";
import { ALL_VAULTS } from "../constant/vaults";
import { ALL_HELPERS } from "../constant/helpers";
import * as Sentry from "@sentry/react";
import controllerAbi from "../../abi/factoryController.json";
import { ALL_CONTROLLERS } from "../constant/pairs";

interface Immutables {
  factory: string;
  token0: string;
  token1: string;
  fee: number;
  tickSpacing: number;
  maxLiquidityPerTick: ethers.BigNumber;
}

interface State {
  liquidity: ethers.BigNumber;
  sqrtPriceX96: ethers.BigNumber;
  tick: number;
  observationIndex: number;
  observationCardinality: number;
  observationCardinalityNext: number;
  feeProtocol: number;
  unlocked: boolean;
}

type tokenProps = {
  tokenSymbol: string;
  tokenFullName: string;
  tokenDecimal: number;
};

const CLOSED_EVENTS = [
  "ClosePosition".toUpperCase(),
  "Liquidate".toUpperCase(),
  "StopLoss".toUpperCase(),
];

const OPEN_EVENTS = ["OpenPosition".toUpperCase()];

const getPriceTick = (
  price: number,
  borrowTokenDecimal: number,
  wantTokenDecimal: number,
  tickSpacing: number,
  type: "floor" | "ceil"
) => {
  return BigNumber.from(
    Math[type](
      Math.log(price / 10 ** (borrowTokenDecimal - wantTokenDecimal)) /
        Math.log(1.0001) /
        tickSpacing
    ) * tickSpacing
  );
};

export const getArbitrumBlockNum = async (timestamp: number) => {
  try {
    const query = `
          {
            blocks(first: 1, orderBy: timestamp, orderDirection: desc, where: { timestamp_gt: ${
              timestamp - 600
            }, timestamp_lt: ${timestamp} }) 
            {
                  number
            },
          }
      `;
    const data = await fetch(
      "https://api.thegraph.com/subgraphs/name/ianlapham/arbitrum-one-blocks",
      {
        body: JSON.stringify({
          query,
          variables: null,
        }),
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    let result = await data.json();

    return result.data.blocks?.[0].number;
  } catch (e) {
    // reached end
    console.error("Fetch all liquidity error", e);
  }
};

export const getUniV3PoolVolume = async (
  blockNum: number,
  poolAddress: string
) => {
  try {
    const query = `
          {
            pools(where: {id_in: ["${poolAddress}"]},
            ${blockNum ? `block: {number: ${blockNum}},` : ""} 
        orderBy: "totalValueLockedUSD", orderDirection: "desc", subgraphError: "allow") {
        volumeUSD
      }
          }
      `;
    const data = await fetch(
      "https://api.thegraph.com/subgraphs/name/ianlapham/arbitrum-dev",
      {
        body: JSON.stringify({
          query,
          variables: null,
        }),
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    let result = await data.json();

    return result.data.pools?.[0]?.volumeUSD || 0;
  } catch (e) {
    // reached end
    console.error("Fetch all liquidity error", e);
  }
};

export const getUniV3PoolFee = async (
  blockNum: number,
  poolAddress: string
) => {
  try {
    const query = `
          {
            pools(where: {id_in: ["${poolAddress}"]},
           block: {number: ${blockNum}},
        orderBy: "totalValueLockedUSD", orderDirection: "desc", subgraphError: "allow") {
        feeUSD
      }
          }
      `;
    const data = await fetch(
      "https://api.thegraph.com/subgraphs/name/ianlapham/arbitrum-minimal",
      {
        body: JSON.stringify({
          query,
          variables: null,
        }),
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    let result = await data.json();

    return result.data.pools?.[0]?.feeUSD || 0;
  } catch (e) {
    // reached end
    console.error("Fetch all liquidity error", e);
  }
};

export const getUniV3PoolStatus = async (
  poolAddress: string,
  tokenA: tokenProps,
  tokenB: tokenProps,
  chainId: number
) => {
  try {
    const provider = isSupportedChain(chainId)
      ? RPC_PROVIDERS[chainId]
      : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

    const poolContract = new ethers.Contract(
      poolAddress,
      uniV3PoolAbi,
      provider
    );

    const getPoolImmutables = async () => {
      const [factory, token0, token1, fee, tickSpacing, maxLiquidityPerTick] =
        await Promise.all([
          poolContract.factory(),
          poolContract.token0(),
          poolContract.token1(),
          poolContract.fee(),
          poolContract.tickSpacing(),
          poolContract.maxLiquidityPerTick(),
        ]);

      const immutables: Immutables = {
        factory,
        token0,
        token1,
        fee,
        tickSpacing,
        maxLiquidityPerTick,
      };
      return immutables;
    };

    const getPoolState = async () => {
      const [liquidity, slot] = await Promise.all([
        poolContract.liquidity(),
        poolContract.slot0(),
      ]);

      const PoolState: State = {
        liquidity,
        sqrtPriceX96: slot[0],
        tick: slot[1],
        observationIndex: slot[2],
        observationCardinality: slot[3],
        observationCardinalityNext: slot[4],
        feeProtocol: slot[5],
        unlocked: slot[6],
      };

      return PoolState;
    };

    const [immutables, state] = await Promise.all([
      getPoolImmutables(),
      getPoolState(),
    ]);

    const TokenA = new Token(
      3,
      immutables.token0,
      tokenA.tokenDecimal,
      tokenA.tokenSymbol,
      tokenA.tokenFullName
    );

    const TokenB = new Token(
      3,
      immutables.token1,
      tokenB.tokenDecimal,
      tokenB.tokenSymbol,
      tokenB.tokenFullName
    );

    const TOKENA_TOKENB_POOL = new Pool(
      TokenA,
      TokenB,
      immutables.fee,
      state.sqrtPriceX96.toString(),
      state.liquidity.toString(),
      state.tick
    );

    const token0Price = TOKENA_TOKENB_POOL.token0Price;

    // Get TVl from Subgraph
    const query = `
          {
            pool(id: "${poolAddress}") {
              totalValueLockedUSD
            }
          }
      `;
    const data = await fetch(
      "https://api.thegraph.com/subgraphs/name/ianlapham/arbitrum-minimal",
      {
        body: JSON.stringify({
          query,
          variables: null,
        }),
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      }
    );
    let result = await data.json();

    return {
      price: +token0Price.toSignificant(18),
      tvl: +result.data.pool?.totalValueLockedUSD || 0,
      feeTier: +immutables.fee,
    };
  } catch (e) {
    // reached end
    console.error("Fetch all liquidity error", e);
  }
};

export const getUniPoolTokenPrices = async (
  poolAddress: string,
  tokenA: tokenProps,
  tokenB: tokenProps,
  chainId: number
) => {
  try {
    const provider = isSupportedChain(chainId)
      ? RPC_PROVIDERS[chainId]
      : RPC_PROVIDERS[SupportedChainId.ARBITRUM_ONE];

    const poolContract = new ethers.Contract(
      poolAddress,
      uniV3PoolAbi,
      provider
    );

    const getPoolImmutables = async () => {
      const [factory, token0, token1, fee, tickSpacing, maxLiquidityPerTick] =
        await Promise.all([
          poolContract.factory(),
          poolContract.token0(),
          poolContract.token1(),
          poolContract.fee(),
          poolContract.tickSpacing(),
          poolContract.maxLiquidityPerTick(),
        ]);

      const immutables: Immutables = {
        factory,
        token0,
        token1,
        fee,
        tickSpacing,
        maxLiquidityPerTick,
      };
      return immutables;
    };

    const getPoolState = async () => {
      const [liquidity, slot] = await Promise.all([
        poolContract.liquidity(),
        poolContract.slot0(),
      ]);

      const PoolState: State = {
        liquidity,
        sqrtPriceX96: slot[0],
        tick: slot[1],
        observationIndex: slot[2],
        observationCardinality: slot[3],
        observationCardinalityNext: slot[4],
        feeProtocol: slot[5],
        unlocked: slot[6],
      };

      return PoolState;
    };

    const [immutables, state] = await Promise.all([
      getPoolImmutables(),
      getPoolState(),
    ]);

    const TokenA = new Token(
      3,
      immutables.token0,
      tokenA.tokenDecimal,
      tokenA.tokenSymbol,
      tokenA.tokenFullName
    );

    const TokenB = new Token(
      3,
      immutables.token1,
      tokenB.tokenDecimal,
      tokenB.tokenSymbol,
      tokenB.tokenFullName
    );

    const TOKENA_TOKENB_POOL = new Pool(
      TokenA,
      TokenB,
      immutables.fee,
      state.sqrtPriceX96.toString(),
      state.liquidity.toString(),
      state.tick
    );

    const token0Price = TOKENA_TOKENB_POOL.token0Price;
    const token1Price = TOKENA_TOKENB_POOL.token1Price;
    const tick = state.tick;

    return {
      token0Price: +token0Price.toSignificant(18),
      token1Price: +token1Price.toSignificant(18),
      tickSpacing: immutables.tickSpacing,
      tick: tick,
    };
  } catch (e) {
    // reached end
    console.error("Fetch all liquidity error", e);
  }
};

export const openFactoryPosition = async (
  userAddress: string,
  factoryAddress: string,
  amount: number,
  tokenAddress: string,
  tokenSymbol: string,
  tokenDecimal: number,
  stopLossUpperPrice: number,
  stopLossLowerPrice: number,
  borrowRatio: number,
  tickRange: number,
  chainId: number,
  borrowTokenDecimal: number,
  tickSpacing: number,
  spotPriceTick: number,
  slippage: number,
  provider: any
) => {
  const openAmount = utils.parseUnits(amount.toString(), tokenDecimal);

  const signer = provider.getSigner(userAddress);
  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);
  const balanceVault = new ethers.Contract(
    ALL_VAULTS[chainId],
    vaultAbi,
    signer
  );
  const controller = new ethers.Contract(
    ALL_CONTROLLERS[chainId],
    controllerAbi,
    signer
  );

  const accountBalance = await balanceVault.getAccountBalance(
    userAddress,
    tokenAddress
  );

  const openPositionMaxAmount = await factory.openPositionMaximumAmount();
  const maxTokenAmount = utils.formatUnits(openPositionMaxAmount, tokenDecimal);

  const openPositionMinAmount = await factory.openPositionMinimumAmount();
  const minTokenAmount = utils.formatUnits(openPositionMinAmount, tokenDecimal);

  const maxPositionNum = await controller.maxPositionNumber();
  const userPositionCount = (await factory.getAccountPositionIds(userAddress))
    .length;
  const reachMaxPosition = userPositionCount >= maxPositionNum;

  const stopLossLowerTick = BigNumber.from(
    Math.ceil(
      Math.log(stopLossLowerPrice / 10 ** (borrowTokenDecimal - tokenDecimal)) /
        Math.log(1.0001) /
        tickSpacing
    ) * tickSpacing
  );

  const stopLossUpperTick = BigNumber.from(
    Math.ceil(
      Math.log(stopLossUpperPrice / 10 ** (borrowTokenDecimal - tokenDecimal)) /
        Math.log(1.0001) /
        tickSpacing
    ) * tickSpacing
  );

  const isApproved = await balanceVault.userApproveFactories(
    userAddress,
    factoryAddress
  );

  if (!isApproved) {
    const approveRes = await balanceVault.approve(factoryAddress, tokenAddress);
    await showPendingAlert(
      approveRes.hash,
      `Approved factory to use asset in your vault.`,
      provider
    );
  }

  if (openAmount.gt(openPositionMaxAmount)) {
    showAlert(
      `Your amount has exceeded the maximum position amount (${+maxTokenAmount} ${tokenSymbol}).`
    );
  } else if (openAmount.lt(openPositionMinAmount)) {
    showAlert(
      `Your amount is lower than the minimum position amount (${+minTokenAmount} ${tokenSymbol}).`
    );
  } else if (accountBalance.lt(openAmount)) {
    showAlert("Your account balance is not enough.");
  } else if (reachMaxPosition) {
    showAlert("Your positions have exceeded the position limit.");
  } else {
    try {
      const proof = await getProof(userAddress);
      const res = await factory.openPosition(
        {
          wantTokenAmount: BigNumber.from(openAmount),
          borrowRatio: BigNumber.from(borrowRatio * 10000),
          spotPriceTick: BigNumber.from(spotPriceTick),
          slippage: BigNumber.from(slippage * 10000),
          reserveRatio: BigNumber.from(0),
          stopLossUpperPriceTick: BigNumber.from(stopLossUpperTick),
          stopLossLowerPriceTick: BigNumber.from(stopLossLowerTick),
          tickRange: BigNumber.from(tickRange * 10000),
        },
        proof
      );
      await showPendingAlert(res.hash, "Open Position Succeed", provider);
    } catch (e: any) {
      if (e.code === "ACTION_REJECTED") {
        showErrorAlert("You denied the transaction.");
      } else if (e.message.includes("04")) {
        showErrorAlert("Lending Pool Balance Is Not Enough.");
      } else if (e.message.includes("08")) {
        showErrorAlert("Open position failed due to slippage protect.");
      } else if (e.message.includes("Not in whitelist")) {
        showErrorAlert("Your account address is not in the whitelist.");
      } else {
        showErrorAlert("Open Position Failed.");
        Sentry.captureException(e);
      }
    }
  }
};

export const getUserActivePositions = async (
  userAddress: string,
  factoryAddress: string,
  wantTokenAddress: string,
  wantTokenDecimal: number,
  borrowTokenAddress: string,
  borrowTokenDecimal: number,
  poolAddress: string,
  lendingPoolAddress: string,
  chainId: number,
  provider: any
) => {
  let positionInfos;

  if (userAddress) {
    const signer = provider.getSigner(userAddress);
    const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);
    const helper = new ethers.Contract(
      ALL_HELPERS[chainId],
      helperAbi,
      provider
    );
    let positionIds;
    try {
      positionIds = await factory.getAccountPositionIds(userAddress);
    } catch (error) {
      // console.log(error);
    }

    if (positionIds?.length > 0) {
      positionInfos = await Promise.all(
        positionIds.map(async (pId: string) => {
          // Get Position Information
          const {
            positionId,
            // address,
            borrowId,
            wantTokenAmountAtStart,
            reserveAmountAtStart,
            positionCreateTimestamp,
            startPriceTick,
            borrowRatio,
            reserveAmount,
            stopLossUpperPriceTick,
            stopLossLowerPriceTick,
          } = await factory.positions(pId);

          const oracleTimeWeightedSec = await factory.oracleTimeWeightedSec();
          const wantTokenIsToken0 = await factory.wantTokenIsToken0();

          // Get Position Token Amount
          const {
            wantTokenAmount,
            borrowTokenAmount,
            wantTokenFee,
            borrowTokenFee,
          } = await helper.getPositionTokenAmount(
            poolAddress,
            pId,
            wantTokenIsToken0
          );

          // Get Debt Vault Measured in Want Token
          const debtValue = await helper.getDebtValueMeasuredInWantToken(
            wantTokenAddress,
            borrowTokenAddress,
            poolAddress,
            lendingPoolAddress,
            borrowId,
            oracleTimeWeightedSec,
            { from: factoryAddress }
          );

          // Get Health Factor
          const healthFactor = await factory.getHealthFactor(pId);

          // Get Tick Range
          const { tickLower, tickUpper } = await getPositionRangesFromV3(
            +pId,
            chainId
          );

          // Get Position Value Measured In Want Token
          const positionValueInWantToken =
            await helper.getPositionValueMeasuredInWantToken(
              wantTokenAddress,
              borrowTokenAddress,
              poolAddress,
              pId,
              wantTokenIsToken0,
              oracleTimeWeightedSec
            );

          const entryPrice =
            1.0001 ** startPriceTick *
            10 ** (borrowTokenDecimal - wantTokenDecimal);

          return {
            borrowId: borrowId.toString(),
            positionId: positionId.toString(),
            stopLossLowerPrice:
              1.0001 ** stopLossLowerPriceTick *
              10 ** (borrowTokenDecimal - wantTokenDecimal),
            stopLossUpperPrice:
              1.0001 ** stopLossUpperPriceTick *
              10 ** (borrowTokenDecimal - wantTokenDecimal),
            wantAmount: utils.formatUnits(
              wantTokenAmountAtStart,
              wantTokenDecimal
            ),
            reserveAmount: utils.formatUnits(reserveAmount, wantTokenDecimal),
            debtValue: utils.formatUnits(debtValue, wantTokenDecimal),
            upperTick: tickUpper.toString(),
            lowerTick: tickLower.toString(),
            upperTickPrice:
              1.0001 ** +tickUpper *
              10 ** (borrowTokenDecimal - wantTokenDecimal),
            lowerTickPrice:
              1.0001 ** +tickLower *
              10 ** (borrowTokenDecimal - wantTokenDecimal),
            entryPrice: entryPrice,
            positionCreateTimestamp: +positionCreateTimestamp.toString(),
            healthFactor: utils.formatUnits(healthFactor, 18),
            principal: utils.formatUnits(borrowTokenAmount, borrowTokenDecimal),
            borrowRatio: +borrowRatio / 10000,

            // NEW
            reserveAmountAtStart: utils.formatUnits(
              reserveAmountAtStart,
              wantTokenDecimal
            ),
            wantTokenFee: utils.formatUnits(wantTokenFee, wantTokenDecimal),
            borrowTokenFee: utils.formatUnits(
              borrowTokenFee,
              borrowTokenDecimal
            ),
            stopLossUpperPriceTick: stopLossUpperPriceTick.toString(),
            stopLossLowerPriceTick: stopLossLowerPriceTick.toString(),
            positionValueInWantToken: utils.formatUnits(
              positionValueInWantToken,
              wantTokenDecimal
            ),
          };
        })
      );
    }
  }

  return positionInfos;
};

export const addFactoryCollateral = async (
  userAddress: string,
  factoryAddress: string,
  tokenAddress: string,
  tokenDecimal: number,
  amount: number,
  positionId: string,
  chainId: number,
  provider: any
) => {
  const tokenAmount = ethers.utils.parseUnits(amount.toString(), tokenDecimal);

  const signer = provider.getSigner(userAddress);
  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);
  const accountBalance = await getUserTokenBalanceInBalanceVault(
    userAddress,
    tokenAddress,
    chainId
  );

  if (accountBalance.lt(tokenAmount)) {
    showAlert("Your account balance is not enough.");
  } else {
    try {
      const proof = await getProof(userAddress);
      const res = await factory.addCollateral(tokenAmount, positionId, proof);
      await showPendingAlert(res.hash, "Add Collateral Succeed", provider);
    } catch (e: any) {
      if (e.code === "ACTION_REJECTED") {
        showErrorAlert("You denied the transaction.");
      } else if (e.message.includes("Not in whitelist")) {
        showErrorAlert("Your account address is not in the whitelist.");
      } else {
        console.log(e);
        showErrorAlert("Add Collateral Failed");
        Sentry.captureException(e);
      }
    }
  }
};

export const closeFactoryPosition = async (
  userAddress: string,
  factoryAddress: string,
  positionId: string,
  spotPriceTick: number,
  slippage: number,
  chainId: number,
  provider: any
) => {
  const signer = provider.getSigner(userAddress);
  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);

  try {
    const proof = await getProof(userAddress);
    const res = await factory.closePosition(
      positionId,
      BigNumber.from(spotPriceTick),
      BigNumber.from(slippage),
      proof
    );
    await showPendingAlert(res.hash, "Close Position Succeed", provider);
  } catch (e: any) {
    if (e.code === "ACTION_REJECTED") {
      showErrorAlert("You denied the transaction.");
    } else if (e.message.includes("08")) {
      showErrorAlert("Close position failed due to slippage protect.");
    } else if (e.message.includes("Not in whitelist")) {
      showErrorAlert("Your account address is not in the whitelist.");
    } else {
      showErrorAlert("Close Position Failed");
      Sentry.captureException(e);
    }
  }
};

export const getUserClosedPositions = async (
  userAddress: string,
  factoryAddress: string,
  wantTokenDecimal: number,
  borrowTokenDecimal: number,
  chainId: number,
  provider: any
) => {
  const closePositionEvents = await getFilteredEventLogs(
    userAddress,
    factoryAddress,
    CLOSED_EVENTS,
    provider
  );

  const closedPositionInfos = await Promise.all(
    closePositionEvents.map(async (history: any) => {
      const {
        positionId,
        // _borrowId,
        startWantAmount,
        reserveWantAmount,
        endWantAmount,
        closePriceTick,
      } = history.args;
      const t = (await provider.getBlock(history.blockNumber)).timestamp;

      return {
        endWantAmount: utils.formatUnits(endWantAmount, wantTokenDecimal),
        positionId: positionId.toString(),
        reserveAmount: utils.formatUnits(reserveWantAmount, wantTokenDecimal),
        startWantAmount: utils.formatUnits(startWantAmount, wantTokenDecimal),
        closePrice:
          1.0001 ** +closePriceTick *
          10 ** (borrowTokenDecimal - wantTokenDecimal),
        eventName: history.name,
        timestamp: t,
        // NEW
        closePriceTick: closePriceTick,
      };
    })
  );

  return closedPositionInfos.reverse();
};

export const getOpenPositionInfoFromLog = async (
  userAddress: string,
  factoryAddress: string,
  poolAddress: string,
  wantTokenDecimal: number,
  borrowTokenDecimal: number,
  chainId: number,
  provider: any
) => {
  const openPositionEvents = await getFilteredEventLogs(
    userAddress,
    factoryAddress,
    OPEN_EVENTS,
    provider
  );

  let positionMap: { [key: string]: { [key: string]: number | string } } = {};

  await Promise.all(
    openPositionEvents.map(async (history: any) => {
      const {
        positionId: id,
        _borrowId,
        startWantAmount,
        reserveWantAmount,
        startPriceTick,
      } = history.args;

      const positionId = id.toString();
      const t = (await provider.getBlock(history.blockNumber)).timestamp;
      const startPrice =
        1.0001 ** +startPriceTick *
        10 ** (borrowTokenDecimal - wantTokenDecimal);
      const borrowedToken = await getUserBorrowAmountFromLogs(
        poolAddress,
        +_borrowId,
        borrowTokenDecimal,
        provider
      );

      const startAmount = +ethers.utils.formatUnits(
        startWantAmount,
        wantTokenDecimal
      );

      positionMap[positionId] = {
        timestamp: t,
        reserveWantAmount: utils.formatUnits(
          reserveWantAmount,
          wantTokenDecimal
        ),
        startPrice: startPrice,
        borrowedEth: borrowedToken,
        borrowedValue: borrowedToken * +startPrice,
        borrowRatio: ((borrowedToken * +startPrice) / startAmount).toFixed(1),
      };

      // get ranges from uni v3
      try {
        const { tickLower, tickUpper } = await getPositionRangesFromV3(
          +positionId,
          chainId
        );
        if (tickLower && tickUpper) {
          positionMap[positionId].upperPrice =
            1.0001 ** +tickUpper *
            10 ** (borrowTokenDecimal - wantTokenDecimal);
          positionMap[positionId].lowerPrice =
            1.0001 ** +tickLower *
            10 ** (borrowTokenDecimal - wantTokenDecimal);
        }
      } catch (error) {
        console.log(error);
      }
    })
  );

  return positionMap;
};

export const getAccountTokenBalance = async (
  userAddress: string,
  chainId: number,
  tokenAddress: string,
  tokenDecimal: number,
  provider: any
) => {
  let tokenBalance = 0;

  if (isSupportedChain(chainId)) {
    const signer = provider.getSigner(userAddress);

    const token = new ethers.Contract(tokenAddress, erc20Abi, signer);

    tokenBalance = +utils.formatUnits(
      await token.balanceOf(userAddress),
      tokenDecimal
    );
  }
  return tokenBalance;
};

export const getPositionRangesFromV3 = async (
  positionId: number,
  chainId: number
) => {
  let ticks;

  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const uniV3 = new ethers.Contract(UNI_POSITION_MANAGER, uniV3Abi, provider);
    ticks = await uniV3.positions(positionId);
  }

  return ticks;
};

export const decreaseFactoryCollateral = async (
  userAddress: string,
  factoryAddress: string,
  amount: number,
  tokenDecimal: number,
  positionId: string,
  chainId: number,
  provider: any
) => {
  const decreaseAmount = utils.parseUnits(amount.toString(), tokenDecimal);

  const signer = provider.getSigner(userAddress);
  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);

  try {
    const proof = await getProof(userAddress);
    const res = await factory.decreaseCollateral(
      decreaseAmount,
      positionId,
      proof
    );
    await showPendingAlert(res.hash, "Decrease Collateral Succeed", provider);
  } catch (e: any) {
    if (e.code === "ACTION_REJECTED") {
      showErrorAlert("You denied the transaction.");
    } else if (e.message.includes("Not in whitelist")) {
      showErrorAlert("Your account address is not in the whitelist.");
    } else if (e.message.includes("11")) {
      showErrorAlert("Your collateral is not enough");
    } else if (e.message.includes("12")) {
      showErrorAlert("Health factor will be too low");
    } else {
      showErrorAlert("Decrease Collateral Failed");
    }
  }
};

export const collectFactoryFee = async (
  userAddress: string,
  factoryAddress: string,
  positionId: string,
  spotPriceTick: number,
  slippage: number,
  chainId: number,
  provider: any
) => {
  const signer = provider.getSigner(userAddress);
  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);

  try {
    const res = await factory.collectFee(
      positionId,
      BigNumber.from(spotPriceTick),
      BigNumber.from(slippage)
    );
    await showPendingAlert(res.hash, "Collect Fee Succeed", provider);
  } catch (e: any) {
    if (e.code === "ACTION_REJECTED") {
      showErrorAlert("You denied the transaction.");
    } else if (e.message.includes("07")) {
      showErrorAlert("This position does not have fee yet.");
    } else {
      showErrorAlert("Collect Fee Failed");
    }
  }
};

export const getUserTokenBalanceInBalanceVault = async (
  userAddress: string,
  tokenAddress: string,
  chainId: number
) => {
  let accountBalance;

  if (isSupportedChain(chainId)) {
    const provider = RPC_PROVIDERS[chainId];
    const signer = provider.getSigner(userAddress);
    const balanceVault = new ethers.Contract(
      ALL_VAULTS[chainId],
      vaultAbi,
      signer
    );

    accountBalance = await balanceVault.getAccountBalance(
      userAddress,
      tokenAddress
    );
  }

  return accountBalance;
};

export const getFilteredEventLogs = async (
  userAddress: string,
  factoryAddress: string,
  eventNames: string[],
  provider: any
) => {
  const filter = {
    address: factoryAddress,
    fromBlock: 0,
  };

  const res = await provider.getLogs(filter);
  const iface = new utils.Interface(factoryV3Abi);

  const parsedEvents = res.map((log: any) => {
    try {
      return { blockNumber: log.blockNumber, ...iface.parseLog(log) };
    } catch (e) {
      return;
    }
  });

  // Filter by Event Name
  const filteredEvents = parsedEvents.filter(
    (e: any) => !!e && eventNames.includes(e.name.toUpperCase())
  );

  // Filter by User Address
  const userEvents = filteredEvents.filter((e: any) =>
    !!userAddress
      ? e.args?.account.toUpperCase() === userAddress.toUpperCase()
      : true
  );

  return userEvents;
};

export const updateFactoryStopLoss = async (
  userAddress: string,
  provider: any,
  factoryAddress: string,
  positionId: string,
  tickSpacing: number,
  wantTokenDecimal: number,
  borrowTokenDecimal: number,
  upperPrice: number,
  lowerPrice: number
) => {
  const upperTick = getPriceTick(
    upperPrice,
    borrowTokenDecimal,
    wantTokenDecimal,
    tickSpacing,
    "ceil"
  );

  const lowerTick = getPriceTick(
    lowerPrice,
    borrowTokenDecimal,
    wantTokenDecimal,
    tickSpacing,
    "floor"
  );

  const signer = provider.getSigner(userAddress);
  const factory = new ethers.Contract(factoryAddress, factoryV3Abi, signer);

  try {
    const proof = await getProof(userAddress);
    const res = await factory.updateStopLossPrice(
      positionId,
      upperTick,
      lowerTick,
      proof
    );
    await showPendingAlert(
      res.hash,
      "Update Stop Loss Prices Succeed",
      provider
    );
  } catch (e: any) {
    if (e.code === "ACTION_REJECTED") {
      showErrorAlert("You denied the transaction.");
    } else {
      showErrorAlert("Update Stop Loss Prices Failed");
    }
    return;
  }

  return;
};
